'use client'
import React, { ReactNode } from 'react'
import { Spinner } from '../../atoms'
import { DataListEmptyState } from './partials'
import { DataListHeader } from './partials/DataListHeader'

export interface DataListProps {
    children: ReactNode
    /** Total number of results, used for built-in header, empty state and accessibility */
    totalResults: number
    /** Text for header, not used if hasCustomHeader is true */
    headerLabel?: ReactNode
    /** Shows loading state if true */
    loading?: boolean
    /** Shows error state if true */
    error?: boolean
    /** Names of columns, used for accessability */
    columnNames?: string[]
    /** Shown if error is true */
    errorState?: ReactNode
    /** Shown if empty is true */
    emptyState?: ReactNode
    /** Prevents use of built-in header */
    hasCustomHeader?: boolean
    /** Makes header sticky, only works with built-in header */
    hasStickyHeader?: boolean
}

export const DataList = ({
    children,
    totalResults,
    headerLabel = 'Results',
    loading = false,
    error = false,
    columnNames,
    errorState = <DataListEmptyState state="error" />,
    emptyState = <DataListEmptyState state="empty" />,
    hasCustomHeader = false,
    hasStickyHeader = false,
}: DataListProps): JSX.Element => {
    const isEmpty: boolean = totalResults === 0
    const isReady: boolean = !isEmpty && !loading && !error
    const headerText = totalResults ? ` (${totalResults})` : ''

    return (
        <div className="overflow-y-auto overflow-x-hidden h-full bg-white">
            {isReady && (
                <ol className="w-full" role="table" aria-rowcount={totalResults}>
                    {columnNames && (
                        <li className="sr-only">
                            <ul role="row">
                                {columnNames.map((column, index) => (
                                    <li key={index} role="columnheader">
                                        {column}
                                    </li>
                                ))}
                            </ul>
                        </li>
                    )}
                    {isReady && !hasCustomHeader && (
                        <DataListHeader isSticky={hasStickyHeader}>
                            {headerLabel}
                            {headerText}
                        </DataListHeader>
                    )}
                    {children}
                </ol>
            )}

            {(isEmpty || error || loading) && (
                <div className="px-6 py-3">
                    {totalResults === 0 && !loading && !error && emptyState}
                    {error && errorState}
                    {loading && <Spinner className="justify-center w-full py-6" />}
                </div>
            )}
        </div>
    )
}
